import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Loader2 } from 'lucide-react';
import { getCsrfToken } from '@/utils/csrf';
import AccountBase from '@/shared/components/accounts/AccountBase';
import { getErrorMessage } from '@/shared/errors';

interface SetPasswordData {
  token: string;
  password: string;
}

const SetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password1, setPassword1] = React.useState('');
  const [password2, setPassword2] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isValidatingToken, setIsValidatingToken] = React.useState(true);

  // Validate token on mount
  React.useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`/api/users/validate-password-token/${token}/`);
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Invalid or expired token');
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsValidatingToken(false);
      }
    };

    if (token) {
      validateToken();
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Basic validation
    if (password1 !== password2) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (password1.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/users/set-password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken() ?? '',
        },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password: password1,
        } as SetPasswordData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to set password');
      }

      setSuccess(true);
      // Navigate after showing success message
      setTimeout(() => {
        navigate('/verification-sent');
      }, 3000);
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Password set error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <AccountBase>
        <div className="container max-w-md mx-auto p-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-center items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <p className="text-sm text-muted-foreground">Validating token...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </AccountBase>
    );
  }

  return (
    <AccountBase>
      <div className="container max-w-md mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-center">Set Your Password</CardTitle>
            <CardDescription className="text-center">
              Create a secure password for your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="mb-6">
                <AlertDescription>
                  Your password has been set successfully. Please check your email to verify your
                  account.
                </AlertDescription>
              </Alert>
            )}

            {!error && !success && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password1">Password</Label>
                  <Input
                    type="password"
                    id="password1"
                    value={password1}
                    onChange={(e) => setPassword1(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password2">Confirm Password</Label>
                  <Input
                    type="password"
                    id="password2"
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting Password...
                    </>
                  ) : (
                    'Set Password'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </AccountBase>
  );
};

export default SetPassword;
